<template>
  <div class="px-8 py-4 bg-white rounded-md shadow-lg">
    <h1 class="mb-6 text-2xl text-gray-700">Edit Rule</h1>
    <vs-popup
      class="rulePopup"
      :title="popup.type === 'add' ? 'Add New Rule' : 'Edit Rule'"
      :active.sync="popup.active"
    >
      <div class="p-4">
        <form class="space-y-3">
          <div class="flex flex-col space-y-2">
            <label for="rName" class="font-bold">Name</label>
            <input
              v-model="popup.data.name"
              id="rName"
              class="w-full px-4 py-2 border border-gray-200 rounded-md"
              type="text"
            />
          </div>
          <div class="flex flex-col space-y-2">
            <label class="font-bold"
              >Language Type (Only for code highligting)</label
            >
            <v-select
              v-model="popup.data.languageType"
              :options="['yaml', 'xml', 'json', 'yara', 'ruby', 'python']"
              @change="prismRendererKey++"
            ></v-select>
          </div>
          <div class="flex flex-col space-y-2">
            <label class="font-bold">Code</label>
            <prism-editor
              class="my-editor"
              v-model="popup.data.codeContent"
              :highlight="highlighter"
              line-numbers
              :key="prismRendererKey"
            ></prism-editor>
          </div>
        </form>
        <div class="flex justify-end mt-6">
          <button
            class="px-6 py-3 text-white bg-blue-500 rounded-md"
            @click.prevent="handleRuleSave()"
          >
            Save
          </button>
        </div>
      </div>
    </vs-popup>
    <form class="space-y-5">
      <div class="flex flex-col space-y-2">
        <label for="name" class="font-bold">Name</label>
        <input
          v-model="form.name"
          id="name"
          class="w-full px-4 py-2 border border-gray-200 rounded-md"
          type="text"
        />
      </div>
      <div class="flex flex-col space-y-2">
        <label for="mitreIds" class="font-bold">Mitre IDs</label>
        <v-select taggable multiple v-model="form.mitreIds"></v-select>
      </div>
      <div class="flex flex-col space-y-2">
        <label for="description" class="font-bold">Description</label>
        <quill-editor
          id="description"
          ref="description"
          v-model="form.description"
          :options="editorOption"
        />
      </div>
      <div class="flex flex-col space-y-2">
        <div class="flex items-center justify-between">
          <label for="rules" class="font-bold">Rules</label>
          <button
            class="px-2 py-1 text-white rounded-md bg-success"
            @click.prevent="handleAddPopup()"
          >
            Add Rule
          </button>
        </div>
        <ul class="px-6 py-4 space-y-4 border bg-gray-50">
          <li
            v-for="(rule, index) in form.rules"
            :key="rule.name"
            class="flex items-center justify-between"
          >
            <p>{{ index + 1 }}. {{ rule.name }}</p>
            <div class="flex space-x-2">
              <button
                class="px-2 py-1 text-sm rounded-md bg-warning"
                @click.prevent="handleEditPopup(index)"
              >
                Edit
              </button>
              <button
                class="px-2 py-1 text-sm text-white bg-red-700 rounded-md"
                @click.prevent="deleteRule(index)"
              >
                Delete
              </button>
            </div>
          </li>
        </ul>
      </div>
      <div class="flex justify-end mt-14">
        <button
          class="px-6 py-3 text-white bg-blue-500 rounded-md"
          @click.prevent="updateRule()"
        >
          Update
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import { PrismEditor } from "vue-prism-editor";
import "vue-prism-editor/dist/prismeditor.min.css";
import "prismjs/components/prism-ruby";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-yaml";
import "prismjs/components/prism-xml-doc";
import "prismjs/components/prism-python";
import "prismjs/components/prism-json";

export default {
  components: {
    vSelect,
    quillEditor,
    PrismEditor,
  },
  data() {
    return {
      prismRendererKey: 0,
      editorOption: {},
      popup: {
        active: false,
        type: null,
        idx: null,
        data: {
          name: null,
          languageType: null,
          codeContent: null,
        },
      },
      form: {
        _id: null,
        name: null,
        mitreIds: [],
        description: null,
        rules: [],
      },
    };
  },
  methods: {
    onEditorChange({ quill }) {
      this.form.description = quill;
    },
    clearPopup() {
      this.popup.active = false;
      this.popup.type = null;
      this.popup.idx = null;
      this.popup.data = {
        name: null,
        languageType: null,
        codeContent: null,
      };
    },
    handleEditPopup(idx) {
      this.clearPopup();
      this.popup.data = JSON.parse(JSON.stringify(this.form.rules[idx]));
      this.popup.idx = idx;
      this.popup.type = "edit";
      this.popup.active = true;
    },
    handleAddPopup() {
      this.clearPopup();
      this.popup.type = "add";
      this.popup.active = true;
    },
    highlighter(code) {
      if (languages[this.popup.data.languageType]) {
        return highlight(code, languages[this.popup.data.languageType]);
      } else {
        return highlight(code, languages.markup);
      }
    },
    handleRuleSave() {
      if (this.popup.type === "add") {
        this.form.rules.push(JSON.parse(JSON.stringify(this.popup.data)));
      } else if (this.popup.type === "edit") {
        this.form.rules[this.popup.idx] = this.popup.data;
      }
      this.clearPopup();
    },
    deleteRule(idx) {
      this.$swal({
        title: "Do you really want to delete the rule?",
        text: "You won't the able to revert this.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it",
      }).then((result) => {
        if (result.value) {
          this.form.rules.splice(idx, 1);
          this.$swal({
            position: "bottom-start",
            icon: "success",
            toast: true,
            title: "Rule successfully deleted.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        }
      });
    },
    updateRule() {
      this.$swal({
        title: "Do you really want to update the rule?",
        text: "You can edit anytime.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, update it",
      }).then((result) => {
        if (result.value) {
          this.$http
            .put(`/management/rules`, { ...this.form })
            .then((res) => {
              if (res.data.status === true) {
                this.$swal({
                  position: "bottom-start",
                  icon: "success",
                  toast: true,
                  title: "Rule successfully updated.",
                  showConfirmButton: false,
                  timer: 3000,
                  timerProgressBar: true,
                });
                this.fetchRuleData(this.form._id);
              } else {
                this.$swal({
                  position: "bottom-start",
                  icon: "error",
                  toast: true,
                  title: "Error occurred updating the rule.",
                  showConfirmButton: false,
                  timer: 3000,
                  timerProgressBar: true,
                });
              }
            })
            .catch((err) => {
              this.$swal({
                icon: "error",
                title: "Error occurred updating the rule.",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
              });
            });
        }
      });
    },
    async fetchRuleData(id) {
      try {
        const response = await this.$http.get("/management/rules", {
          params: {
            id: id,
          },
        });
        let {_id, name, mitreIds, description, rules} = response.data.result;
        this.form = {_id, name, mitreIds, description, rules};
      } catch (e) {}
    },
  },
  computed: {
    editor() {
      return this.$refs.description.quill;
    },
  },
  created() {
    this.fetchRuleData(this.$route.params.id);
  },
};
</script>

<style>
/* required class */
.my-editor {
  /* we dont use `language-` classes anymore so thats why we need to add background and text color manually */
  background: #2d2d2d;
  color: #ccc;

  /* you must provide font-family font-size line-height. Example: */
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
  max-height: 450px;
}

.vs-popup {
  width: 1000px !important;
}

/* optional class for removing the outline */
.prism-editor__textarea:focus {
  outline: none;
}
</style> 